
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Loader2, Check } from 'lucide-react';
import { api, Position, Store, Staff } from '../../api';
import Modal from '../ui/Modal';
import Dialog from '../ui/Dialog';
import Input from '../ui/Input';
import { useToast } from '../../contexts/ToastContext';

const COLORS = [ 
  { label: '青', value: 'bg-blue-100 text-blue-800' }, 
  { label: '緑', value: 'bg-green-100 text-green-800' }, 
  { label: '赤', value: 'bg-red-100 text-red-800' }, 
  { label: '黄', value: 'bg-yellow-100 text-yellow-800' }, 
  { label: '紫', value: 'bg-purple-100 text-purple-800' }, 
  { label: '橙', value: 'bg-orange-100 text-orange-800' }, 
  { label: '灰', value: 'bg-gray-100 text-gray-800' } 
];

interface PositionSettingsTabProps {
  stores: Store[];
  myProfile: Staff | null;
  isSystemAdmin: boolean;
}

const PositionSettingsTab: React.FC<PositionSettingsTabProps> = ({ stores: allStores, myProfile, isSystemAdmin }) => {
  const { showToast } = useToast();
  const [positions, setPositions] = useState<Position[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterStoreId, setFilterStoreId] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState<Partial<Position>>({});
  const [dialog, setDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  const labelClass = "text-xs font-bold text-slate-500 mb-1.5 block uppercase tracking-wider";

  const availableStores = allStores.filter(s => isSystemAdmin || myProfile?.stores?.includes(s.id));

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const pos = await api.getPositions();
      setPositions(pos);
    } catch (e) {
      showToast('データの取得に失敗しました。', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    if (!editingPosition.name || !editingPosition.storeIds || editingPosition.storeIds.length === 0) {
        showToast('ポジション名と店舗を選択してください。', 'error');
        return;
    }
    setIsLoading(true);
    try {
      const pos = {
        id: editingPosition.id || `pos_${Date.now()}`,
        name: editingPosition.name,
        color: editingPosition.color || COLORS[0].value,
        storeIds: editingPosition.storeIds
      } as Position;
      await api.savePositions([pos]);
      fetchData();
      setIsModalOpen(false);
      showToast('保存しました。', 'success');
    } catch (e) {
      showToast('保存に失敗しました。', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (id: string) => {
    setDialog({
      isOpen: true,
      title: 'ポジション削除',
      message: 'このポジションを削除しますか？',
      onConfirm: async () => {
        try {
          await api.deletePosition(id);
          setPositions(prev => prev.filter(p => p.id !== id));
          showToast('削除しました。', 'success');
        } catch (e) {
          showToast('削除に失敗しました。', 'error');
        }
        setDialog(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const filteredPositions = positions.filter(p => {
      const isMemberOfStore = p.storeIds?.some(sid => availableStores.some(as => as.id === sid));
      const matchesFilter = filterStoreId === 'all' || p.storeIds?.includes(filterStoreId);
      return isMemberOfStore && matchesFilter;
  });

  return (
    <div className="space-y-6 animate-fade-in-up">
      <Dialog {...dialog} type="confirm" isDestructive onCancel={() => setDialog(prev => ({ ...prev, isOpen: false }))} />
      
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-bold text-slate-800">ポジション一覧</h2>
          <select value={filterStoreId} onChange={(e) => setFilterStoreId(e.target.value)} className="bg-white border border-gray-300 rounded-[4px] px-3 py-2 text-sm font-bold text-slate-600">
            <option value="all">全店舗を表示</option>
            {availableStores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <button onClick={() => { setEditingPosition({ color: COLORS[0].value, storeIds: [availableStores[0]?.id] }); setIsModalOpen(true); }} className="bg-blue-600 text-white font-bold px-4 py-2 rounded-[4px] text-sm flex items-center shadow-md active:scale-95"><Plus size={16} className="mr-1"/>新規作成</button>
      </div>

      <div className="bg-white shadow-sm border border-gray-200 overflow-hidden rounded-[4px]">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 text-slate-500 font-bold border-b border-gray-200 text-xs uppercase">
            <tr><th className="p-4">ポジション名</th><th className="p-4">対象店舗</th><th className="p-4">カラー</th><th className="p-4 text-center">操作</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr><td colSpan={4} className="p-8 text-center text-slate-400"><Loader2 className="animate-spin inline mr-2" size={20}/>読み込み中...</td></tr>
            ) : filteredPositions.length === 0 ? (
              <tr><td colSpan={4} className="p-8 text-center text-slate-400 font-bold text-xs uppercase tracking-widest">ポジション設定がありません</td></tr>
            ) : filteredPositions.map(pos => (
              <tr key={pos.id} className="hover:bg-gray-50 transition-colors">
                <td className="p-4 font-bold text-slate-800">{pos.name}</td>
                <td className="p-4 text-slate-500 text-xs">{pos.storeIds?.map(sid => availableStores.find(s => s.id === sid)?.name).filter(Boolean).join(', ')}</td>
                <td className="p-4"><div className={`w-6 h-6 rounded-[4px] border border-black/10 ${pos.color.split(' ')[0]}`} /></td>
                <td className="p-4 text-center">
                  <div className="flex justify-center gap-2">
                    <button onClick={() => { setEditingPosition(pos); setIsModalOpen(true); }} className="p-2 text-blue-600 hover:bg-blue-50 rounded-[4px]"><Edit size={18} /></button>
                    <button onClick={() => handleDelete(pos.id)} className="p-2 text-gray-400 hover:text-red-600 rounded-[4px]"><Trash2 size={18} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="ポジション設定" maxWidth="max-w-md">
        <div className="space-y-6 pt-2">
          <Input label="ポジション名" value={editingPosition.name || ''} placeholder="例：ホール" onChange={(e) => setEditingPosition({...editingPosition, name: e.target.value})} />
          
          <div>
            <label className={labelClass}>対象店舗</label>
            <div className="flex flex-wrap gap-2 p-3 bg-slate-50 border border-slate-200 rounded-[4px]">
                {availableStores.map(store => {
                    const isSelected = editingPosition.storeIds?.includes(store.id);
                    return (
                        <button
                            key={store.id}
                            onClick={() => {
                                const current = editingPosition.storeIds || [];
                                const next = isSelected ? current.filter(id => id !== store.id) : [...current, store.id];
                                setEditingPosition({ ...editingPosition, storeIds: next });
                            }}
                            className={`px-3 py-1.5 rounded-[4px] text-[10px] font-bold border transition-all ${isSelected ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}
                        >
                            {store.name}
                        </button>
                    );
                })}
            </div>
          </div>

          <div>
            <label className={labelClass}>カラー</label>
            <div className="grid grid-cols-4 gap-2">
              {COLORS.map(c => <button key={c.value} onClick={() => setEditingPosition({...editingPosition, color: c.value})} className={`h-10 rounded-[4px] border-2 transition-all flex items-center justify-center ${editingPosition.color === c.value ? 'border-blue-600 ring-2 ring-blue-100' : 'border-transparent'} ${c.value}`}>{editingPosition.color === c.value && <Check size={16} />}</button>)}
            </div>
          </div>
          <div className="pt-6 flex justify-end gap-3 border-t">
            <button onClick={() => setIsModalOpen(false)} className="px-6 py-2.5 text-slate-500 font-bold">キャンセル</button>
            <button onClick={handleSave} disabled={isLoading} className="px-8 py-2.5 bg-blue-600 text-white font-bold rounded-[4px] shadow-md flex items-center gap-2">
              {isLoading && <Loader2 size={16} className="animate-spin" />}保存
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PositionSettingsTab;
